import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Ticket, Gift, Copy } from "lucide-react";

const SITE = window.location.origin;
const EMPTY = { code: "", kind: "gift", value: 10, expires_at: "", max_uses: "", min_item_price: 150, valid_countries: "", gift_product_ids: [], description: "", active: true };

const testLink = (code) => `${SITE}/?coupon=${code}`;
const copy = (txt) => navigator.clipboard.writeText(txt).then(() => toast.success("Link copiato"));

function GiftPicker({ gifts, selected, onChange }) {
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="flex flex-wrap gap-2" data-testid="gift-picker">
      {gifts.map((g) => (
        <label key={g.id} className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-xs cursor-pointer ${selected.includes(g.id) ? "border-emerald-500 bg-emerald-50" : "border-xd-border"}`}>
          <input type="checkbox" checked={selected.includes(g.id)} onChange={() => toggle(g.id)} data-testid={`gift-pick-${g.id}`} />
          {g.images?.[0] && <img src={g.images[0]} alt="" className="w-8 h-8 rounded object-cover" />}
          <span>{g.name} <span className="text-xd-muted">(listino € {Number(g.compare_at_price || 0).toFixed(2)})</span></span>
        </label>
      ))}
    </div>
  );
}

function CouponForm({ gifts, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const create = async () => {
    if (!form.code) return toast.error("Codice richiesto");
    try {
      await api.post("/admin/coupons", {
        ...form,
        value: form.kind === "gift" ? 0 : Number(form.value),
        expires_at: form.expires_at || null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        min_item_price: Number(form.min_item_price || 150),
        valid_countries: form.valid_countries.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean),
        gift_product_ids: form.kind === "gift" ? form.gift_product_ids : [],
      });
      toast.success("Coupon creato");
      setForm(EMPTY);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="bg-white border border-xd-border rounded-xl p-5 mb-6 space-y-3">
      <div className="font-admin font-semibold">Crea nuovo codice</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Input placeholder="Codice (es. OMAGGIO)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, "") })} data-testid="coupon-code-input" />
        <select value={form.kind} onChange={set("kind")} data-testid="coupon-kind-select" className="border border-xd-border rounded-md px-3 py-2 text-sm">
          <option value="gift">Omaggio (prodotti gratis)</option>
          <option value="percent">Percentuale</option>
          <option value="fixed">Importo fisso</option>
        </select>
        {form.kind === "gift"
          ? <Input type="number" placeholder="Prezzo min. autoradio €" value={form.min_item_price} onChange={set("min_item_price")} data-testid="coupon-min-price-input" title="Gli omaggi si attivano se nel carrello c'è un prodotto con prezzo superiore a questo valore" />
          : <Input type="number" placeholder="Valore" value={form.value} onChange={set("value")} data-testid="coupon-value-input" />}
        <Input type="date" value={form.expires_at} onChange={set("expires_at")} data-testid="coupon-expires-input" title="Scadenza (inclusa)" />
        <Input type="number" placeholder="Max utilizzi (vuoto = illimitati)" value={form.max_uses} onChange={set("max_uses")} data-testid="coupon-max-uses-input" />
        <Input placeholder="Paesi validi (es. IT,DE) — vuoto = tutti" value={form.valid_countries} onChange={set("valid_countries")} data-testid="coupon-countries-input" />
        <Input className="md:col-span-2" placeholder="Note interne (es. Campagna Meta giugno)" value={form.description} onChange={set("description")} data-testid="coupon-desc-input" />
      </div>
      {form.kind === "gift" && <GiftPicker gifts={gifts} selected={form.gift_product_ids} onChange={(ids) => setForm({ ...form, gift_product_ids: ids })} />}
      <button type="button" onClick={create} data-testid="coupon-create-btn" className="btn-cta rounded-lg text-sm inline-flex items-center gap-2 justify-center px-5 py-2.5"><Plus className="w-4 h-4" /> Crea codice</button>
    </div>
  );
}

function CouponRow({ c, gifts, onToggle, onDelete }) {
  const giftNames = (c.gift_product_ids || []).map((id) => gifts.find((g) => g.id === id)?.name || id).join(" + ");
  const value = c.kind === "gift" ? giftNames : c.kind === "percent" ? `${c.value}%` : `€ ${c.value}`;
  return (
    <tr data-testid={`coupon-row-${c.code}`} className="border-b border-xd-border last:border-b-0 hover:bg-slate-50 align-top">
      <td className="px-4 py-3 font-mono-tech font-semibold">
        <div className="flex items-center gap-2">{c.kind === "gift" ? <Gift className="w-4 h-4 text-emerald-600" /> : <Ticket className="w-4 h-4 text-xd-orange" />} {c.code}</div>
        {c.description && <div className="text-[11px] font-sans font-normal text-xd-muted mt-1">{c.description}</div>}
      </td>
      <td className="px-4 py-3 text-xs">{c.kind === "gift" ? `Omaggio (> € ${c.min_item_price ?? 150})` : c.kind}</td>
      <td className="px-4 py-3 text-xs">{value}</td>
      <td className="px-4 py-3 text-xs font-mono-tech" data-testid={`coupon-uses-${c.code}`}>{c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
      <td className="px-4 py-3 text-xs">{c.expires_at ? c.expires_at.slice(0, 10) : "—"}</td>
      <td className="px-4 py-3 text-xs">{(c.valid_countries || []).join(", ") || "Tutti"}</td>
      <td className="px-4 py-3 text-xs">
        <button type="button" onClick={() => onToggle(c)} data-testid={`coupon-toggle-${c.code}`} className={`px-2 py-0.5 rounded ${c.active ? "bg-xd-success/10 text-xd-success" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Attivo" : "Disattivo"}</button>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button type="button" onClick={() => copy(testLink(c.code))} title={testLink(c.code)} data-testid={`coupon-copy-link-${c.code}`} className="inline-flex items-center justify-center w-10 h-10 mr-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-xd-border"><Copy className="w-4 h-4" /></button>
        <button type="button" onClick={() => onDelete(c.id)} data-testid={`coupon-delete-${c.code}`} className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 border border-red-200 transition-colors"><Trash2 className="w-5 h-5" /></button>
      </td>
    </tr>
  );
}

export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [gifts, setGifts] = useState([]);
  const load = () => api.get("/admin/coupons").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/admin/gift-products").then((r) => setGifts(r.data)); }, []);

  const toggle = async (c) => {
    try { await api.patch(`/admin/coupons/${c.id}`, { active: !c.active }); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare questo codice?")) return;
    try { await api.delete(`/admin/coupons/${id}`); toast.success("Eliminato"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">Marketing</div>
        <h1 className="font-admin text-2xl font-bold">Codici sconto e omaggio</h1>
        <p className="text-xs text-xd-muted mt-1">Link pubblicità: <code>{SITE}/?coupon=CODICE</code> · Checkout Meta: <code>{SITE}/checkout?products=ID:1&amp;coupon=CODICE</code></p>
      </div>

      <CouponForm gifts={gifts} onCreated={load} />

      <div className="bg-white border border-xd-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-xd-light border-b border-xd-border"><tr>{["Codice", "Tipo", "Valore / Omaggi", "Utilizzi", "Scadenza", "Paesi", "Stato", ""].map((h) => <th key={h} className="px-4 py-3 text-left font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">{h}</th>)}</tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-xd-muted">Nessun coupon</td></tr>}
            {items.map((c) => <CouponRow key={c.id} c={c} gifts={gifts} onToggle={toggle} onDelete={del} />)}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
